import type { AgentState } from '../../lib/schema';
import { AGENTS_BY_ID } from '../../lib/agents';

interface Props {
  readonly states: readonly AgentState[];
  readonly lastSync: number | null;
  readonly theme: 'day' | 'night';
}

function formatNumber(n: number | undefined): string {
  if (n === undefined) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

const STATUS_TONE: Record<AgentState['status'], string> = {
  idle: 'var(--color-deck-muted)',
  working: 'var(--color-deck-cyan)',
  waiting: 'var(--color-deck-warn)',
  blocked: 'var(--color-deck-alert)',
  offline: 'var(--color-deck-dim)',
  celebrating: 'var(--color-deck-success)',
};

export function Hud({ states, lastSync, theme }: Props) {
  const totalSessions = states.reduce(
    (a, s) => a + (s.kpis?.activeSessions ?? 0),
    0,
  );
  const totalToolCalls = states.reduce(
    (a, s) => a + (s.kpis?.toolCalls ?? 0),
    0,
  );
  const totalTokens = states.reduce(
    (a, s) => a + (s.kpis?.totalTokens ?? 0),
    0,
  );
  const workingCount = states.filter((s) => s.status === 'working').length;
  const liveCount = states.filter(
    (s) => s.status !== 'offline' && s.status !== 'blocked',
  ).length;
  const progress = Math.min(100, (workingCount / states.length) * 100);

  return (
    <>
      {/* Scan bar + grain handled at Station layout level */}

      {/* Top bar */}
      <header
        className="absolute top-0 left-0 right-0 z-10 pointer-events-none enter"
        style={{ padding: '20px 28px' }}
      >
        <div className="flex items-start justify-between gap-8">
          <div className="flex items-center gap-4 pointer-events-auto">
            <span
              className="lum-dot"
              style={{ color: 'var(--color-deck-cyan)' }}
              aria-hidden="true"
            />
            <div className="flex flex-col gap-1">
              <span
                className="t-display"
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: 'var(--color-deck-ink)',
                }}
              >
                COMMAND DECK
                <span
                  style={{
                    color: 'var(--color-deck-cyan)',
                    marginLeft: 8,
                    animation: 'ticker-blink 1s steps(2) infinite',
                  }}
                >
                  ▎
                </span>
              </span>
              <span className="t-tag">
                <span style={{ color: 'var(--color-deck-cyan)' }}>
                  {String(liveCount).padStart(2, '0')}
                </span>
                <span style={{ color: 'var(--color-deck-dim)' }}>
                  /{String(states.length).padStart(2, '0')}
                </span>{' '}
                · NODES ONLINE · UPLINK STABLE · {theme === 'night' ? 'NIGHT' : 'DAY'} PROTOCOL
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3 pointer-events-auto">
            <a href="/text" className="pill" style={{ textDecoration: 'none', color: 'var(--color-deck-muted)' }}>
              /text
            </a>
            <a href="/record" className="pill" style={{ textDecoration: 'none', color: 'var(--color-deck-muted)' }}>
              /record
            </a>
            {lastSync && (
              <span className="t-mono" style={{ fontSize: 10, color: 'var(--color-deck-muted)', padding: '0 4px' }}>
                <span style={{ color: 'var(--color-deck-cyan)' }}>◆</span>{' '}
                {new Date(lastSync).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Hero KPI — bottom-left */}
      <aside
        className="notched-frame notched-frame--amber z-10 pointer-events-auto enter"
        style={{
          position: 'absolute',
          bottom: 28,
          left: 28,
          padding: '22px 26px 20px',
          minWidth: 340,
          animationDelay: '0.1s',
        }}
      >
        <span className="bracket bracket--tl bracket--amber" />
        <span className="bracket bracket--br bracket--amber" />

        <div className="t-tag" style={{ marginBottom: 6 }}>
          <span
            className="lum-dot"
            style={{
              color: 'var(--color-deck-amber)',
              width: '0.4em',
              height: '0.4em',
              marginRight: 8,
            }}
          />
          ACTIVE CLAUDE CODE SESSIONS
        </div>

        <div
          className="t-hero-num"
          style={{
            fontSize: 54,
            lineHeight: 1,
            marginBottom: 16,
            color: 'var(--color-deck-amber)',
            textShadow:
              '0 0 24px color-mix(in oklch, var(--color-deck-amber) 40%, transparent)',
          }}
        >
          {totalSessions}
          <span
            style={{
              fontSize: 16,
              color: 'var(--color-deck-muted)',
              marginLeft: 10,
              fontWeight: 400,
            }}
          >
            live
          </span>
        </div>

        <div className="progress-shell">
          <div
            className="progress-fill"
            style={{
              width: `${progress}%`,
              background: 'var(--color-deck-amber)',
              boxShadow:
                '0 0 12px color-mix(in oklch, var(--color-deck-amber) 65%, transparent), inset 0 0 4px oklch(1 0 0 / 0.3)',
            }}
          />
        </div>

        <div
          className="flex justify-between"
          style={{
            marginTop: 8,
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '0.12em',
            fontVariantNumeric: 'tabular-nums',
            textTransform: 'uppercase',
          }}
        >
          <span style={{ color: 'var(--color-deck-amber)' }}>
            {formatNumber(totalToolCalls)} tool calls
          </span>
          <span style={{ color: 'var(--color-deck-muted)' }}>
            {formatNumber(totalTokens)} tokens
          </span>
        </div>
      </aside>

      {/* Activity Stream — bottom-center */}
      <aside
        className="notched-frame z-10 pointer-events-auto enter"
        style={{
          position: 'absolute',
          bottom: 28,
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '18px 22px',
          minWidth: 440,
          animationDelay: '0.15s',
        }}
      >
        <span className="bracket bracket--tl" />
        <span className="bracket bracket--br" />

        <div className="t-tag" style={{ color: 'var(--color-deck-cyan)', marginBottom: 12 }}>
          ◆ ACTIVITY STREAM · REALTIME
        </div>

        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {states
            .filter((s) => s.status !== 'offline')
            .slice(0, 4)
            .map((s, idx) => {
              const cfg = AGENTS_BY_ID[s.agentId];
              if (!cfg) return null;
              const tone = STATUS_TONE[s.status];
              return (
                <li
                  key={s.agentId}
                  className="flex items-center gap-3"
                  style={{
                    padding: '5px 0',
                    borderBottom:
                      idx < 3 ? '1px solid oklch(0.30 0.020 250 / 0.25)' : 'none',
                    opacity: 1 - idx * 0.15,
                  }}
                >
                  <span
                    className="t-mono"
                    style={{
                      color: 'var(--color-deck-dim)',
                      fontSize: 10,
                      minWidth: 40,
                      letterSpacing: '0.08em',
                    }}
                  >
                    {new Date().toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  <span
                    className="lum-dot"
                    style={{
                      color: tone,
                      width: '0.4em',
                      height: '0.4em',
                    }}
                  />
                  <span
                    className="t-mono"
                    style={{
                      color: tone,
                      minWidth: 62,
                      fontSize: 10,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {cfg.label}
                  </span>
                  <span
                    style={{
                      color: 'var(--color-deck-ink-soft)',
                      fontSize: 12,
                      fontFamily: 'var(--font-sans)',
                      letterSpacing: '-0.005em',
                      flex: 1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {s.activity}
                  </span>
                </li>
              );
            })}
        </ul>
      </aside>

      {/* Roster — bottom-right */}
      <aside
        className="notched-frame z-10 pointer-events-auto enter"
        style={{
          position: 'absolute',
          bottom: 28,
          right: 28,
          padding: '18px 22px',
          minWidth: 288,
          animationDelay: '0.2s',
        }}
      >
        <span className="bracket bracket--tr" />
        <span className="bracket bracket--bl" />

        <div className="t-tag" style={{ color: 'var(--color-deck-cyan)', marginBottom: 12 }}>
          ◆ AGENT ROSTER
        </div>

        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {states.map((s, idx) => {
            const cfg = AGENTS_BY_ID[s.agentId];
            if (!cfg) return null;
            const tone = STATUS_TONE[s.status];
            return (
              <li
                key={s.agentId}
                className="flex items-center gap-3"
                style={{
                  padding: '9px 0',
                  borderBottom:
                    idx < states.length - 1
                      ? '1px solid oklch(0.30 0.020 250 / 0.20)'
                      : 'none',
                }}
              >
                <span
                  className="lum-dot"
                  style={{
                    color: tone,
                    width: '0.42em',
                    height: '0.42em',
                    opacity: s.status === 'offline' ? 0.45 : 1,
                  }}
                />
                <span
                  className="t-display"
                  style={{
                    color: 'var(--color-deck-ink)',
                    flex: 1,
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >
                  {cfg.label}
                </span>
                <span
                  className="t-mono"
                  style={{
                    color: 'var(--color-deck-muted)',
                    fontSize: 9,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                  }}
                >
                  {cfg.title}
                </span>
              </li>
            );
          })}
        </ul>
      </aside>
    </>
  );
}
